import { useCallback, useEffect, useState } from 'react';
import { DIKKAT_BOS_METIN, JOB_STATUS_ETIKET } from '@mod/domain';
import { BilgiIkon, IndirIkon, SaatIkon, SiralaIkon, UyariIkon } from '../lib/ikon';
import { DisaAktarKutu } from './DisaAktar';
import { useKaliciDurum } from '../lib/kalici';
import { metrajGoster } from '../lib/olcu';
import { api } from '../lib/api';
import { useOturum } from '../lib/oturum';

/**
 * ŞŞ-01 — ŞANTİYE PANOSU (devir dokumani Bolum 6.1, prototip `vPano`).
 * Sefin gune basladigi ANA EKRAN. Kapsam TEK MUSTERI (Nuh Cimento).
 *
 * Tasarim dili: endustriyel, yogun-veri, dusuk-dekorasyon. "Guzel" degil, OKUNUR.
 * Renkler SEMANTIK (Bolum 4.2) — dekoratif degil.
 */

const KOVA = [
  { k: 'kesif_bekleyen', e: 'Keşif bekleyen', renk: '#9C6A08' },
  { k: 'hazirlikta', e: 'Hazırlıkta', renk: '#54657A' },
  { k: 'yapimda', e: 'Yapımda', renk: '#1F3B63' },
  { k: 'kontrolde', e: 'Kontrolde', renk: '#1C6C82' },
  { k: 'tamamlanan', e: 'Tamamlanan', renk: '#1C7346' },
] as const;

const KOVA_DURUMLAR: Record<string, string[]> = {
  kesif_bekleyen: ['giris'],
  hazirlikta: ['kesif', 'hazirlik'],
  yapimda: ['kurulum', 'revize', 'sokum'],
  kontrolde: ['kontrol'],
  tamamlanan: ['ayakta', 'kapali'],
};

/*
 * ⚠️ ETİKETLER ARTIK @mod/domain'DE (`JOB_STATUS_ETIKET`), burada yalnız RENK.
 *    Gerekçe: aynı etiket dışa aktarımda (Excel/PDF) da basılıyor ve müşteriye
 *    giden dosya ekrandakinden farklı konuşamaz. Renk ise yalnız bu ekranın
 *    tasarım diline ait — sunucuya taşınacak bir şey değil.
 *
 * Tamamlanan işlerin etiketleri MÜŞTERİNİN DİLİYLE (istek 07.08.2026):
 * "Kuruldu" · "Söküldü" · "Revizyon" — işin BAŞINA NE GELDİĞİ okunuyor.
 */
const DURUM_RENK: Record<string, string> = {
  giris: 'bg-warn/10 text-warn',
  kesif: 'bg-info/10 text-info',
  hazirlik: 'bg-stok/10 text-stok',
  kurulum: 'bg-navy/10 text-navy',
  kontrol: 'bg-info/15 text-info',
  ayakta: 'bg-ok/10 text-ok',
  revize: 'bg-stok/15 text-stok',
  sokum: 'bg-stok/15 text-stok',
  kapali: 'bg-surface-2 text-wait',
  ertelendi: 'bg-warn/15 text-warn',
  iptal: 'bg-surface-2 text-wait',
};

interface IsSatir {
  id: string;
  isEmriNo: string | null;
  /** Müşterinin iskeleyi andığı numara — aynı iş emrinde 29 iskele olabiliyor. */
  iskeleOnayNo: string | null;
  tanim: string;
  unite: string;
  ilgili: string[];
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde (pano metriği, aylık kümülatif). */
  esdegerM3: number;
  durum: string;
  isEmriFotoVar: boolean;
  yesilKart: boolean;
  kuruluGun: number | null;
  olusturmaZamani: string;
  /** SOCAR: SAP önceliği (1-6). K6 — yalnız SÜZGEÇ, sıralamaya girmez. */
  oncelik?: number | null;
  /** SOCAR: ünite grubu (STAR / Petkim / iç / dış proje). */
  uniteGrup?: string | null;
}

interface DikkatKarti {
  jobId: string | null;
  tetikleyici: string;
  sinif: 'kritik' | 'uyari' | 'bilgi';
  baslik: string;
  detay: string;
  /** Toplu kartın kapsadığı iş sayısı — yalnız haftalık kontrolde dolu. */
  adet?: number;
}

interface Panel {
  kovalar: { anahtar: string; sayi: number }[];
  kurulu: {
    m3: number;
    adet: number;
    acilis: number;
    kurulan: number;
    sokulen: number;
    /** §6.1b — sahadaki malzeme tonajı. Rapor künyesinden gelir, hesaplanmaz. */
    ton: number | null;
  };
  donem: string;
  isler: IsSatir[];
  dikkat: DikkatKarti[];
  ertelemeSaat: number;
}

/** N ay önceki dönem anahtarı (YYYY-MM). */
const AY = (geri: number) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - geri);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Bugünün tarihi, <input type="date"> biçiminde — YEREL, UTC değil. */
const BUGUN = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmt = (n: number) =>
  n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });

export function Pano({
  onIsAc,
  onIsSec,
  onRapor,
  onKontrolListesi,
}: {
  onIsAc: () => void;
  onIsSec: (id: string) => void;
  onRapor: () => void;
  /** Toplu haftalık kontrol kartı — vadesi geçmiş iskele listesine götürür. */
  onKontrolListesi: () => void;
}) {
  const { ben } = useOturum();
  /*
   * MÜŞTERİ YETKİLİSİ — pano OKUNUR, "bugün dikkat gerektiren" YOK
   * (kullanıcı kararı 07.09.2026). Sunucu o listeyi zaten boş döndürüyor;
   * burada bölümün kendisi çizilmiyor: boş bir "dikkat" başlığı, müşteriye
   * anlamı olmayan bir Mod iç kuyruğunu ima ederdi.
   */
  const musteri = ben?.musteri ?? false;
  const [panel, setPanel] = useState<Panel | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /*
   * ⚠️ EKRAN AYARLARI SAYFA GEÇİŞİNDE KORUNUR (`useKaliciDurum`, 05.08.2026).
   *
   * Panodan bir işe girip geri dönmek bu ekranı unmount/mount ediyor; sıradan
   * `useState` ile filtre de, sayfa da, sıralama da başa dönüyordu. 976 kayıtta
   * bu, bulunan işi yeniden aramak demekti. Kalıcılık SEKME ÖMRÜ kadar —
   * gerekçesi `lib/kalici.ts`'te.
   *
   * `panel` ve `hata` BİLEREK kalıcı değil: biri sunucudan gelen veri, diğeri
   * anlık bir durum. Kalıcı olsalardı kullanıcı eski veriyi ya da geçmiş bir
   * hatayı taze sanırdı.
   */
  // Kova = FILTRE (kullanici geri bildirimi: "sayıya tıklayınca hangi işler olduğu görülmeli").
  const [filtre, setFiltre] = useKaliciDurum<string | null>('pano.filtre', null);
  const [hepsi, setHepsi] = useKaliciDurum('pano.hepsi', false);
  /** §4.5 — dönem YALNIZ "Tamamlanan" kovasına uygulanır; diğerleri anlık. */
  const [donem, setDonem] = useKaliciDurum('pano.donem', 'tumu');
  /**
   * ÖZEL ARALIK — hazır seçimler ay bazlı, bu gün bazlı.
   * "Ayın 10'undan 25'ine ne teslim ettik" hazır seçimlerle sorulamıyor;
   * müşteriye kesilen ara dönem raporları da tam ay olmuyor.
   */
  /**
   * SAYFALAMA — 976 kayıtlık gerçek veriden sonra zorunlu oldu.
   * Tarayıcı tek tabloda binlerce satırı çiziyor ve sayfa donuyordu.
   */
  const [sayfa, setSayfa] = useKaliciDurum('pano.sayfa', 1);
  const [boy, setBoy] = useKaliciDurum('pano.boy', 25);
  /** Kurulum tarihine göre — varsayılan YENİDEN ESKİYE: panoda "bugün ne oldu". */
  const [yon, setYon] = useKaliciDurum<'asc' | 'desc'>('pano.yon', 'desc');
  /*
   * ÖNCELİK ve ÜNİTE GRUBU SÜZGEÇLERİ (SOCAR, 09.10.2026). Öncelik süzgeci
   * yalnız SAP alanlı müşteride, grup süzgeci gruplanmış ünite varsa çizilir;
   * Nuh ve Akçansa'nın panosu değişmez.
   *
   * ⚠️ ÖNCELİK SIRALAMAZ (K6). Müşteri: *"sıra geliş sırasına göre devam etsin,
   *    ama filtre uygulanabilir olsun."* Süzgeç listeyi daraltır, sırayı değil.
   */
  const [oncelikSuz, setOncelikSuz] = useKaliciDurum('pano.oncelik', '');
  const [grupSuz, setGrupSuz] = useKaliciDurum('pano.grup', '');
  const [bas, setBas] = useKaliciDurum('pano.bas', '');
  const [bit, setBit] = useKaliciDurum('pano.bit', '');
  /* Dışa aktarım katmanı — KALICI DEĞİL: açık bir kutunun sayfa geçişinden
     sağ çıkması, kullanıcının kapattığını sandığı bir işi geri getirirdi. */
  const [aktarim, setAktarim] = useState(false);
  const aralikModu = donem === 'aralik';
  const aralikHazir = aralikModu && bas !== '' && bit !== '';

  const yukle = useCallback(() => {
    // Aralık seçili ama iki uç da girilmemişse SORGU ATMA — yarım aralıkla
    // dönen boş liste, kullanıcıya "hiç iş yok" gibi görünürdü.
    if (aralikModu && !aralikHazir) return;
    const q = aralikHazir ? `donem=aralik&bas=${bas}&bit=${bit}` : `donem=${donem}`;
    api
      .get<Panel>(`/panel?${q}`)
      .then(setPanel)
      .catch((e: Error) => setHata(e.message));
  }, [donem, bas, bit, aralikModu, aralikHazir]);

  useEffect(yukle, [yukle]);

  /**
   * Kural seti Bölüm 6 — tıklanınca ilgili ekrana gider:
   * kontrol → onay ekranı (iş detayı), keşif/malzeme → iş detayı,
   * rapor → günlük rapor.
   */
  const kartaGit = (k: DikkatKarti) => {
    if (k.tetikleyici === 'gunluk_rapor') return onRapor();
    /*
     * TOPLU HAFTALIK KONTROL KARTI bir işe değil LİSTEYE gider (07.09.2026).
     * Hedef "Kurulu İskeleler"in vadesi geçmiş görünümü: aynı süzgeç orada
     * zaten var (`?vade=gecti`), ikinci bir liste ekranı yazmak aynı kuralın
     * iki yerde ayrışması demekti.
     */
    if (k.tetikleyici === 'haftalik_kontrol' && !k.jobId) return onKontrolListesi();
    if (k.jobId) onIsSec(k.jobId);
  };

  const ertele = async (k: DikkatKarti) => {
    await api.post('/dikkat/ertele', { tetikleyici: k.tetikleyici, jobId: k.jobId });
    yukle();
  };

  if (hata)
    return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  if (!panel) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  const sap = !!ben?.santiye.sapAlanlari;
  const gruplar = [
    ...new Set(panel.isler.map((j) => j.uniteGrup).filter((g): g is string => !!g)),
  ].sort((a, b) => a.localeCompare(b, 'tr'));
  const suzulmus = panel.isler
    .filter((j) => !filtre || KOVA_DURUMLAR[filtre]!.includes(j.durum))
    .filter((j) => !sap || !oncelikSuz || String(j.oncelik ?? '') === oncelikSuz)
    .filter((j) => !gruplar.length || !grupSuz || (j.uniteGrup ?? '') === grupSuz);

  /*
   * Sıralama ve sayfalama İSTEMCİDE: sunucu zaten K6 FIFO gönderiyor ve
   * ağa gitmek çevrimdışında tabloyu kullanılamaz yapardı (T1).
   */
  const z = (j: IsSatir) => new Date(j.olusturmaZamani).getTime();
  const sirali = [...suzulmus].sort((a, b) => (yon === 'asc' ? z(a) - z(b) : z(b) - z(a)));

  const sonSayfa = Math.max(1, Math.ceil(sirali.length / boy));
  // Filtre/boy değişince mevcut sayfa listenin dışında kalabilir; sona sabitle.
  const aktifSayfa = Math.min(sayfa, sonSayfa);
  const gosterilen = sirali.slice((aktifSayfa - 1) * boy, aktifSayfa * boy);

  const dikkat = hepsi ? panel.dikkat : panel.dikkat.slice(0, 6);

  return (
    <div className="mx-auto max-w-[1400px] p-4 lg:p-6">
      {/* Dönem — §4.5: yalnız "Tamamlanan" kovasını süzer. */}
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2 text-sm">
        <span className="text-xs text-wait">
          Dönem <span className="opacity-70">(yalnız “Tamamlanan”)</span>
        </span>
        <select
          value={donem}
          onChange={(e) => {
            const d = e.target.value;
            setDonem(d);
            // Aralığa geçerken alanlar boşsa bugünle doldur: takvimin bir yere
            // sabitlenmiş olması, boş iki kutudan daha az düşündürür.
            if (d === 'aralik' && !bas && !bit) {
              setBas(BUGUN());
              setBit(BUGUN());
            }
          }}
          className="rounded-(--radius-mod) border border-line-2 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-navy"
        >
          <option value="tumu">Tümü</option>
          {/* Gün bazlı hazır seçimler — sunucu bunları özel aralıkla AYNI
              yoldan işler (saha saati +03). Müşteri isteği 07.08.2026. */}
          <option value="bugun">Bugün</option>
          <option value="dun">Dün</option>
          <option value={AY(0)}>Bu ay</option>
          <option value={AY(1)}>Geçen ay</option>
          <option value="sonyil">Son 12 ay</option>
          <option value="aralik">Tarih aralığı…</option>
        </select>

        {aralikModu && (
          <span className="flex items-center gap-1.5">
            <input
              type="date"
              value={bas}
              max={bit || undefined}
              onChange={(e) => setBas(e.target.value)}
              aria-label="Başlangıç tarihi"
              className="num rounded-(--radius-mod) border border-line-2 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-navy"
            />
            <span className="text-wait">–</span>
            <input
              type="date"
              value={bit}
              min={bas || undefined}
              onChange={(e) => setBit(e.target.value)}
              aria-label="Bitiş tarihi"
              className="num rounded-(--radius-mod) border border-line-2 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-navy"
            />
          </span>
        )}
      </div>

      {/* ── KOVA ŞERİDİ — bitişik, aralarında ince ayraç (prototip) ── */}
      <div className="mb-4 grid grid-cols-2 overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) sm:grid-cols-3 lg:grid-cols-5">
        {KOVA.map((b, i) => {
          const sayi = panel.kovalar.find((x) => x.anahtar === b.k)?.sayi ?? 0;
          const aktif = filtre === b.k;
          return (
            <button
              key={b.k}
              onClick={() => {
                setFiltre(aktif ? null : b.k);
                setSayfa(1);
              }}
              className={`px-5 py-4 text-left transition ${
                i > 0 ? 'lg:border-l lg:border-neutral-200' : ''
              } ${aktif ? 'bg-navy/5' : 'hover:bg-ground'}`}
            >
              <span className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: b.renk }} />
                <span style={{ color: b.renk }}>{b.e}</span>
              </span>
              <span className="num mt-1 block text-3xl leading-none font-semibold text-ink">
                {sayi}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── SAHADA KURULU İSKELE ── */}
      <div className="mb-6 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-5 shadow-(--shadow-mod)">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <span className="text-[11px] font-semibold tracking-wider text-wait uppercase">
            Sahada kurulu iskele
          </span>
          <span className="text-sm text-wait">
            <span className="num font-semibold text-ink">{panel.kurulu.adet}</span> aktif
            iskele
            {panel.kurulu.ton != null && (
              <>
                {' · '}
                <span className="num font-semibold text-ink">{fmt0(panel.kurulu.ton)}</span>{' '}
                ton
              </>
            )}
          </span>
        </div>
        <p className="mt-1 flex items-baseline gap-2">
          <span className="num text-4xl leading-none font-semibold text-navy">
            {fmt0(panel.kurulu.m3)}
          </span>
          <span className="text-sm text-wait">m³ kurulu</span>
        </p>

        {/*
          İŞLETME SERMAYESİ MANTIĞI (Bölüm 6.1b): açılış + giren − çıkan = kapanış.
          ⚠️ K7 — FİNANSAL DEĞİL; Nuh'ta kira yok, takip ve müşteri raporu verisi.
        */}
        <div className="mt-4 flex flex-wrap items-center gap-1.5 text-sm">
          <Akis e="Ay açılış" v={panel.kurulu.acilis} />
          <Op>+</Op>
          <Akis e="Kurulan" v={panel.kurulu.kurulan} renk="text-ok" />
          <Op>−</Op>
          <Akis e="Sökülen" v={panel.kurulu.sokulen} renk="text-warn" />
          <Op>=</Op>
          <Akis e="Bugün" v={panel.kurulu.m3} vurgu />
        </div>
      </div>

      {/* ── BUGÜN DİKKAT GEREKTİREN ── (müşteride hiç çizilmez) */}
      {!musteri && (
      <section className="mb-6">
        <Bolum baslik="Bugün dikkat gerektiren" sayi={panel.dikkat.length} kritik />
        {panel.dikkat.length === 0 ? (
          /* Kural seti Bölüm 7 — bölüm GİZLENMEZ. Boş olduğunu görmek de bilgidir:
             "kart yok" ile "pano yüklenmedi" ayırt edilebilmeli. */
          <p className="rounded-(--radius-mod) border border-ok/25 bg-ok/5 px-4 py-6 text-center text-sm text-ok">
            {DIKKAT_BOS_METIN}
          </p>
        ) : (
          <div className="grid gap-2.5 lg:grid-cols-2">
            {dikkat.map((k, i) => (
              <Dikkat key={i} k={k} onSec={kartaGit} onErtele={ertele} saat={panel.ertelemeSaat} />
            ))}
          </div>
        )}
        {panel.dikkat.length > 6 && (
          <button
            onClick={() => setHepsi((v) => !v)}
            className="mt-2.5 text-xs font-medium text-wait hover:text-navy"
          >
            {hepsi ? '− daha az göster' : `+${panel.dikkat.length - 6} daha`}
          </button>
        )}
      </section>
      )}

      {/* ── İŞLER ── */}
      <Bolum
        baslik="İşler"
        sayi={sirali.length}
        birim="kayıt"
        sag={
          <button
            onClick={() => setAktarim(true)}
            title="İş listesini Excel veya PDF olarak indir"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-(--radius-mod) border border-line-2 px-2.5 py-1 text-xs font-medium text-wait hover:border-navy hover:text-navy"
          >
            <IndirIkon boyut={14} />
            Dışa Aktar
          </button>
        }
      >
        {filtre && (
          <button
            onClick={() => setFiltre(null)}
            className="rounded-full bg-navy/8 px-2.5 py-1 text-xs font-medium text-navy"
          >
            {KOVA.find((b) => b.k === filtre)?.e} ✕
          </button>
        )}
        {sap && (
          <select
            value={oncelikSuz}
            onChange={(e) => {
              setOncelikSuz(e.target.value);
              setSayfa(1);
            }}
            aria-label="Önceliğe göre süz"
            className="rounded-(--radius-mod) border border-line-2 px-2 py-1 text-xs text-wait"
          >
            <option value="">Öncelik: hepsi</option>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                Öncelik {n}
              </option>
            ))}
          </select>
        )}
        {gruplar.length > 0 && (
          <select
            value={grupSuz}
            onChange={(e) => {
              setGrupSuz(e.target.value);
              setSayfa(1);
            }}
            aria-label="Ünite grubuna göre süz"
            className="rounded-(--radius-mod) border border-line-2 px-2 py-1 text-xs text-wait"
          >
            <option value="">Grup: hepsi</option>
            {gruplar.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => setYon((v) => (v === 'asc' ? 'desc' : 'asc'))}
          title={
            yon === 'desc'
              ? 'En yeni üstte — tersine çevirmek için tıklayın'
              : 'En eski üstte (FIFO) — tersine çevirmek için tıklayın'
          }
          aria-label={`Tarihe göre sıralama: ${yon === 'desc' ? 'en yeni üstte' : 'en eski üstte'}`}
          className="inline-flex items-center gap-1.5 rounded-(--radius-mod) border border-line-2 px-2.5 py-1 text-xs font-medium text-wait hover:border-navy hover:text-navy"
        >
          <SiralaIkon yon={yon} boyut={14} />
          {yon === 'desc' ? 'Yeni → eski' : 'Eski → yeni'}
        </button>
        <select
          value={boy}
          onChange={(e) => {
            setBoy(Number(e.target.value));
            setSayfa(1);
          }}
          aria-label="Sayfa başına kayıt"
          className="rounded-(--radius-mod) border border-line-2 bg-white px-2 py-1 text-xs outline-none focus:border-navy"
        >
          {[25, 50, 100, 250].map((n) => (
            <option key={n} value={n}>
              {n}/sayfa
            </option>
          ))}
        </select>
      </Bolum>

      <div className="overflow-x-auto rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) shadow-(--shadow-mod)">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[11px] font-semibold tracking-wider text-wait uppercase">
              <th className="px-4 py-2.5">İş Emri</th>
              {/* Onay no AYRI SÜTUN — aynı iş emri altında 29 iskele olabiliyor
                  ve müşteri iskeleyi bu numarayla anıyor (istek 07.08.2026). */}
              <th className="px-4 py-2.5">Onay No</th>
              <th className="px-4 py-2.5">İş</th>
              <th className="px-4 py-2.5">Ünite</th>
              <th className="px-4 py-2.5">Tip</th>
              <th className="px-4 py-2.5">İlgili</th>
              <th className="px-4 py-2.5 text-right">Metraj</th>
              <th className="px-4 py-2.5">Durum</th>
            </tr>
          </thead>
          <tbody>
            {gosterilen.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-wait">
                  {filtre ? 'Bu kovada iş yok.' : 'Henüz iş açılmamış.'}
                </td>
              </tr>
            )}
            {gosterilen.map((j) => {
              const renk = DURUM_RENK[j.durum] ?? 'bg-surface-2 border-line-2 text-wait';
              return (
                <tr
                  key={j.id}
                  onClick={() => onIsSec(j.id)}
                  className="cursor-pointer border-b border-line/70 last:border-0 hover:bg-ground"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      {/* K3 — kâğıt fotosu eksikse iş kapanamaz */}
                      {!j.isEmriFotoVar && (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-warn"
                          title="İş emri fotosu eksik"
                        />
                      )}
                      <span className="num font-medium text-navy">{j.isEmriNo ?? '—'}</span>
                    </span>
                    <span className="num block text-[11px] text-wait">
                      {new Date(j.olusturmaZamani).toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                      })}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {/* Teslimde giriliyor — bekleyen işte henüz YOKTUR, "—" görünür. */}
                    <span className="num text-ink-2">{j.iskeleOnayNo ?? '—'}</span>
                  </td>
                  <td className="px-4 py-3">{j.tanim}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-ink-2">{j.unite}</td>
                  <td className="px-4 py-3">
                    <span className="rounded bg-navy/8 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-navy uppercase">
                      {j.durum === 'sokum' ? 'Söküm' : j.durum === 'revize' ? 'Revize' : 'Kurulum'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-ink-2">
                    {j.ilgili[0] ?? '—'}
                    {j.ilgili.length > 1 && (
                      <span className="num ml-1 text-xs text-wait">+{j.ilgili.length - 1}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {j.metrajM3 || j.metrajM2 ? (
                      <span className="num text-base font-semibold">{metrajGoster(j)}</span>
                    ) : (
                      <span className="text-wait italic">keşifte</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`rozet ${renk}`}>
                      {JOB_STATUS_ETIKET[j.durum as keyof typeof JOB_STATUS_ETIKET] ?? j.durum}
                    </span>
                    {j.durum === 'ayakta' && !j.yesilKart && (
                      <span className="ml-1.5 rounded-full bg-crit/15 px-2 py-1 text-xs font-medium text-crit">
                        yeşil kart yok
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/*
          SAYFA GEZİNME — 976 kayıtlık gerçek veriden sonra zorunlu.
          "Son" düğmesi bilinçli: en eski kayıtlara ulaşmak için 39 kez
          "sonraki"ye basmak, listeyi fiilen erişilemez kılıyordu.
        */}
        {sonSayfa > 1 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-line bg-surface px-4 py-2.5 text-sm">
            <span className="num text-xs text-wait">
              {(aktifSayfa - 1) * boy + 1}–{Math.min(aktifSayfa * boy, sirali.length)} /{' '}
              {sirali.length}
            </span>
            <span className="ml-auto flex items-center gap-1">
              {(
                [
                  ['« İlk', 1, aktifSayfa === 1],
                  ['‹ Önceki', aktifSayfa - 1, aktifSayfa === 1],
                ] as const
              ).map(([e, hedef, kapali]) => (
                <button
                  key={e}
                  disabled={kapali}
                  onClick={() => setSayfa(hedef)}
                  className="rounded-(--radius-mod) border border-line-2 px-2.5 py-1 text-xs font-medium text-wait hover:border-navy hover:text-navy disabled:opacity-35"
                >
                  {e}
                </button>
              ))}
              <span className="num px-2 text-xs">
                {aktifSayfa} / {sonSayfa}
              </span>
              {(
                [
                  ['Sonraki ›', aktifSayfa + 1, aktifSayfa === sonSayfa],
                  ['Son »', sonSayfa, aktifSayfa === sonSayfa],
                ] as const
              ).map(([e, hedef, kapali]) => (
                <button
                  key={e}
                  disabled={kapali}
                  onClick={() => setSayfa(hedef)}
                  className="rounded-(--radius-mod) border border-line-2 px-2.5 py-1 text-xs font-medium text-wait hover:border-navy hover:text-navy disabled:opacity-35"
                >
                  {e}
                </button>
              ))}
            </span>
          </div>
        )}

        {/* Alt bilgi — projenin değer önermesi burada görünür */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-line bg-ground/60 px-4 py-2.5 text-xs text-wait">
          <span>
            Bu ay kurulan <strong className="num text-ok">+{fmt(panel.kurulu.kurulan)} m³</strong>
          </span>
          <span>
            Sökülen <strong className="num text-warn">−{fmt(panel.kurulu.sokulen)} m³</strong>
          </span>
          <span className="ml-auto">
            {/* K6 hâlâ geçerli: öncelik ALANI yok. Sıralama bir GÖRÜNÜM tercihi. */}
            Öncelik alanı yok (K6) — sıralama yalnız görünüm · satırlar günlük rapora
            otomatik akar
          </span>
        </div>
      </div>

      {/* İş açmak Mod'un işi — müşteride bu düğme hiç çizilmez. */}
      {!musteri && (
        <div className="mt-4 lg:hidden">
          <button
            onClick={onIsAc}
            className="w-full rounded-(--radius-mod) bg-navy px-4 py-3 text-sm font-medium text-white"
          >
            + İş Aç
          </button>
        </div>
      )}

      {aktarim && <DisaAktarKutu onKapat={() => setAktarim(false)} />}
    </div>
  );
}

function Op({ children }: { children: React.ReactNode }) {
  return <span className="px-0.5 text-base text-wait">{children}</span>;
}

function Akis({ e, v, renk, vurgu }: { e: string; v: number; renk?: string; vurgu?: boolean }) {
  return (
    <span
      className={`rounded-(--radius-mod) px-3 py-2 text-center ${
        vurgu ? 'bg-navy/8 ring-1 ring-navy/15' : 'bg-surface-2'
      }`}
    >
      <span className={`num block leading-tight font-semibold ${renk ?? 'text-ink'}`}>
        {fmt0(v)}
      </span>
      <span className="block text-[10px] tracking-wider text-wait uppercase">{e}</span>
    </span>
  );
}

function Bolum({
  baslik,
  sayi,
  birim,
  kritik,
  children,
  sag,
}: {
  baslik: string;
  sayi: number;
  birim?: string;
  kritik?: boolean;
  children?: React.ReactNode;
  /** Çizginin SAĞINDA duran eylem — başlığın yanındaki süzgeçlerle karışmasın. */
  sag?: React.ReactNode;
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2.5">
      <h2 className={`font-semibold ${kritik ? 'text-crit' : 'text-ink'}`}>{baslik}</h2>
      <span className="num text-sm text-wait">
        {sayi}
        {birim && ` ${birim}`}
      </span>
      {children}
      <span className="h-px flex-1 bg-neutral-200" />
      {sag}
    </div>
  );
}

/** Dikkat kartı — sınıf rengi sol şeritte ve ikon kutusunda tekrarlanır. */
function Dikkat({
  k,
  onSec,
  onErtele,
  saat,
}: {
  k: DikkatKarti;
  onSec: (k: DikkatKarti) => void;
  onErtele: (k: DikkatKarti) => Promise<void>;
  saat: number;
}) {
  const s =
    k.sinif === 'kritik'
      ? {
          kart: 'border-crit-line bg-crit-bg/40 border-l-crit',
          ic: 'bg-crit text-white',
          bas: 'text-crit',
          ikon: <UyariIkon boyut={17} />,
        }
      : k.sinif === 'uyari'
        ? {
            kart: 'border-warn-line bg-warn-bg/40 border-l-warn',
            ic: 'bg-warn-bg text-warn',
            bas: 'text-warn',
            ikon: <SaatIkon boyut={17} />,
          }
        : {
            kart: 'border-line bg-surface border-l-wait',
            ic: 'bg-wait-bg text-wait',
            bas: 'text-ink',
            ikon: <BilgiIkon boyut={17} />,
          };

  // Kural seti Bölüm 6: kritik ERTELENEMEZ. Sunucu da reddeder (T6) —
  // burada düğmeyi hiç çizmemek gerekçeyi görünür kılar.
  const ertelenebilir = k.sinif !== 'kritik';

  return (
    <div
      className={`group flex w-full items-start gap-3 rounded-(--radius-kart) border border-l-[5px] p-3.5 shadow-(--shadow-mod) ${s.kart}`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] ${s.ic}`}
      >
        {s.ikon}
      </span>
      <button onClick={() => onSec(k)} className="min-w-0 flex-1 text-left">
        <span className={`flex items-center gap-2 text-sm leading-tight font-bold ${s.bas}`}>
          <span className="truncate">{k.baslik}</span>
          {/*
            Sayı rozeti — kart artık tek bir işi değil bir GRUBU temsil ediyor.
            Başlığın yanında küçük durur; kartın kendisi büyümemeli, yoksa
            toplamanın amacı olan "pano sadeleşsin" kaybolur.
          */}
          {k.adet != null && (
            <span className="shrink-0 rounded-full bg-wait-bg px-1.5 py-0.5 text-[11px] font-semibold text-wait tabular-nums">
              {k.adet}
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-[11px] leading-snug text-muted">{k.detay}</span>
      </button>
      {ertelenebilir && (
        <button
          onClick={() => void onErtele(k)}
          title={`${saat} saat ertele`}
          className="shrink-0 rounded-(--radius-mod) px-2 py-1 text-xs text-wait opacity-0 transition group-hover:opacity-100 hover:bg-surface-2 hover:text-navy focus:opacity-100"
        >
          {saat}s ertele
        </button>
      )}
    </div>
  );
}
